import {CorrespondenceType} from "../../definitions/types";
import {CorrespondenceModel} from "../domain/correspondenceModel";

export class ArticlesGuidesModel {
    private readonly _articles: CorrespondenceModel[];
    private readonly _guides: CorrespondenceModel[];

    constructor(public readonly articlesTypes: CorrespondenceType[], public readonly guidesTypes: CorrespondenceType[]) {
        this._articles = articlesTypes.map(article => new CorrespondenceModel(article));
        this._guides = guidesTypes.map(guide => new CorrespondenceModel(guide));
    }

    /**
     * @method resources
     * @description Returns all resources (articles and guides) for the product in an array.
     * @returns {CorrespondenceModel[]}
     */
    public get resources(): CorrespondenceModel[] {
        return [
            ...this._articles,
            ...this._guides
        ].sort((a, b) => a.sort - b.sort);
    }

    /**
     * @method resourceCount
     * @description Returns the total number of resources (articles and guides) for the product.
     * @returns {number}
     */
    public get resourceCount(): number {
        return this.articleCount + this.guideCount;
    }

    /**
     * @method hasResources
     * @description Returns true if the product has any resources (articles or guides).
     * @returns {boolean}
     */
    public get hasResources(): boolean {
        return this.resourceCount > 0;
    }

    /**
     * @method articles
     * @description Returns all articles for the product in an array.
     * @returns {CorrespondenceModel[]}
     */
    public get articles(): CorrespondenceModel[] {
        return this._articles;
    }

    /**
     * @method articleCount
     * @description Returns the total number of articles for the product.
     * @returns {number}
     */
    public get articleCount(): number {
        return this._articles.length;
    }

    /**
     * @method hasArticles
     * @description Returns true if the product has any articles.
     * @returns {boolean}
     */
    public get hasArticles(): boolean {
        return this._articles.length > 0;
    }

    /**
     * @method guides
     * @description Returns all guides for the product in an array.
     * @returns {CorrespondenceModel[]}
     */
    public get guides(): CorrespondenceModel[] {
        return this._guides;
    }

    /**
     * @method guideCount
     * @description Returns the total number of guides for the product.
     * @returns {number}
     */
    public get guideCount(): number {
        return this._guides.length;
    }

    /**
     * @method hasGuides
     * @description Returns true if the product has any guides.
     * @returns {boolean}
     */
    public get hasGuides(): boolean {
        return this._guides.length > 0;
    }

    /**
     * @method title
     * @description Returns the title for the correspondence section of the product page.
     * @returns {string}
     */
    public get title(): string {
        if (this.hasArticles && this.hasGuides) return 'Articles & Guides';
        if (this.hasArticles) return 'Articles';
        return 'Guides';
    }
}